/**
 * The one menu-visibility predicate.
 *
 * Before this file the rule lived in three places — an inline lambda in `hooks/menu.ts`,
 * a second copy in `layout/menu/components/items.tsx`, and a third reimplementation
 * inside `hooks/menu.test.ts` (so the test could pass while the app was wrong). Every
 * consumer that decides what a user may see — the sidebar, the mobile menu, the route
 * guard, the command palette, the coverage diagnostics — imports from here, because a
 * palette entry that leads to a denied route is the exact failure this centralisation
 * exists to make impossible.
 */

import type { IMenuItem, IMenuSub } from '@/layout/utils';

import { hasAny, hasAnyRole, isObserverAdmin, isSuperAdmin } from './authority';
import type { IPermissionSnapshot } from './types';

export interface IMenuSection {
	section: string;
	items: IMenuItem[];
}

export const isMenuItemVisible = (item: IMenuItem, snapshot: IPermissionSnapshot): boolean =>
	!item.hide &&
	(!item.superAdminOnly || isSuperAdmin(snapshot)) &&
	(!item.observerAdminOnly || isObserverAdmin(snapshot)) &&
	(!item.requiredRoles?.length || hasAnyRole(snapshot, item.requiredRoles)) &&
	(!item.excludedRoles?.length || !hasAnyRole(snapshot, item.excludedRoles)) &&
	hasAny(snapshot, item.roleActions);

export const isMenuSubVisible = (sub: IMenuSub, snapshot: IPermissionSnapshot): boolean =>
	!sub.hide && (!sub.superAdminOnly || isSuperAdmin(snapshot)) && hasAny(snapshot, sub.roleActions);

export const visibleSubItems = (item: IMenuItem, snapshot: IPermissionSnapshot): IMenuSub[] =>
	item.sub?.filter((sub) => isMenuSubVisible(sub, snapshot)) ?? [];

/**
 * Group the permitted items into their declared sections, preserving first-appearance
 * order. A section whose items are all filtered out disappears with them — a lone
 * heading over nothing reads as a loading failure.
 */
export const buildVisibleMenu = (menu: readonly IMenuItem[], snapshot: IPermissionSnapshot): IMenuSection[] => {
	const sections: IMenuSection[] = [];
	const bySection = new Map<string, IMenuItem[]>();

	menu
		.filter((item) => isMenuItemVisible(item, snapshot))
		.forEach((item) => {
			const section = item.section ?? '';
			if (!bySection.has(section)) {
				const items: IMenuItem[] = [];
				bySection.set(section, items);
				sections.push({ section, items });
			}
			bySection.get(section)!.push(item);
		});

	return sections;
};

/** Absolute path of a sub-item. `url: ''` targets the parent itself. */
export const subItemPath = (item: IMenuItem, sub: IMenuSub): string => (sub.url ? `${item.pageUrl}/${sub.url}` : item.pageUrl);

/**
 * Segment-aware active check.
 *
 * Replaces `_isUrl` (`components/reusable/helper.ts`), which is
 * `pathname.includes(page)` and has two live defects: `_isUrl('')` is always true —
 * hit by the two sub-items declaring `url: ''` — and a bare segment like `'users'`
 * matches both `/admin/management/users` and `/admin/vesr/users`, lighting two menu
 * items at once.
 */
export const isPathActive = (target: string, pathname: string): boolean => {
	if (!target) return false;
	return pathname === target || pathname.startsWith(`${target}/`);
};

/**
 * The single deepest menu path matching the location, so exactly one item renders
 * active. Longest match wins: `/admin/elections/manage-agents` resolves to Manage
 * Agents rather than also lighting Elections.
 */
export const activeMenuPath = (menu: readonly IMenuItem[], pathname: string): string | null => {
	let best: string | null = null;

	const consider = (candidate: string) => {
		if (!isPathActive(candidate, pathname)) return;
		if (best === null || candidate.length > best.length) best = candidate;
	};

	menu.forEach((item) => {
		consider(item.pageUrl);
		item.sub?.forEach((sub) => consider(subItemPath(item, sub)));
	});

	return best;
};

export interface IDestination {
	/** Ancestor titles, e.g. `['Situation Room']`. Empty for a top-level item. */
	trail: string[];
	/** Product-language name of the destination, or a neutral fallback. */
	label: string;
	path: string;
	/** Codes that would open it — OR semantics. Empty when the path is unknown. */
	requiredActions: readonly string[];
	/** True when the path matched a real menu entry. */
	isKnown: boolean;
}

/**
 * Describe a path in the words the product uses, for the access-denied page.
 *
 * Walks the UNFILTERED menu on purpose: the whole job here is naming something the
 * user cannot see. Longest-prefix match, so a detail route inherits its module's name.
 */
export const describeDestination = (pathname: string, menu: readonly IMenuItem[]): IDestination => {
	let best: IDestination | null = null;

	const consider = (candidate: IDestination) => {
		if (!isPathActive(candidate.path, pathname)) return;
		if (best === null || candidate.path.length > best.path.length) best = candidate;
	};

	menu.forEach((item) => {
		consider({
			trail: [],
			label: item.title,
			path: item.pageUrl,
			requiredActions: item.roleActions,
			isKnown: true,
		});

		item.sub?.forEach((sub) => {
			// A `url: ''` sub shares the parent's path; the parent already describes it.
			if (!sub.url) return;
			consider({
				trail: [item.title],
				label: sub.title,
				path: subItemPath(item, sub),
				requiredActions: sub.roleActions,
				isKnown: true,
			});
		});
	});

	return (
		best ?? {
			trail: [],
			label: 'this page',
			path: pathname,
			requiredActions: [],
			isKnown: false,
		}
	);
};
